import { useMemo, useCallback } from 'react';
import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import { mapValues, isEmpty } from 'mojito/utils';
import BonusIndicatorUtils from './utils.js';
import BonusIndicatorTypes from './types.js';

const { ACCA_BOOST, EVENT_TRIGGERED, ODDS_BOOST, EARLY_PAYOUT, ENHANCED_EW_TERMS } =
    BonusIndicatorTypes.TYPES;
const { LabeledTooltip, FlexPane } = MojitoPresentation.Components;
const { dispatch } = MojitoCore.Services.redux.store;
const intentActions = MojitoCore.Intents.actions;

const BonusIndicator = props => {
    const { title, text, bonusType, bonusInfo, currencyCode, isAwarded, disabled, mojitoTools } =
        props;
    const { config, style, stringResolver } = mojitoTools;
    const intent = !isEmpty(bonusType) && config.bonusTypes[bonusType]?.readMoreIntent;

    const onTooltipButtonClick = useCallback(() => {
        if (intent) {
            dispatch(intentActions.publishIntent(intent.type, intent.data));
        }
    }, [intent]);

    const tooltipContent = useMemo(
        () =>
            !isEmpty(bonusType) && {
                title: title || getBonusTitle(bonusType, stringResolver, bonusInfo),
                text: text || getBonusText(bonusType, currencyCode, stringResolver, bonusInfo),
                linkText: intent
                    ? stringResolver.resolveString(`$BONUS_INDICATOR.${bonusType}.LINK_TEXT`)
                    : undefined,
                additionalInfo: isAwarded
                    ? stringResolver.resolveString(`$BONUS_INDICATOR.${bonusType}.AWARDED_TEXT`)
                    : undefined,
            },
        [title, text, bonusType, isAwarded, stringResolver, bonusInfo, currencyCode, intent]
    );
    const showTooltipButton = !!(tooltipContent.linkText && intent);

    if (config.hide || isEmpty(bonusType)) {
        return null;
    }

    return (
        <FlexPane class={`ta-BonusIndicator-${bonusType}`} config={config.container}>
            <LabeledTooltip
                config={style.labeledTooltips[bonusType]}
                title={tooltipContent.title}
                additionalInfo={tooltipContent.additionalInfo}
                tooltipText={tooltipContent.text}
                tooltipButtonText={tooltipContent.linkText}
                onTooltipButtonClick={onTooltipButtonClick}
                showTooltipButton={showTooltipButton}
                disabled={disabled}
            />
        </FlexPane>
    );
};

const getBonusTitle = (bonusType, stringResolver, bonusInfo) => {
    switch (bonusType) {
        case ODDS_BOOST: {
            return BonusIndicatorUtils.getBoostTitle(bonusType, bonusInfo, stringResolver);
        }
        case ACCA_BOOST:
            return BonusIndicatorUtils.getAccaBoostTitle(bonusType, bonusInfo, stringResolver);
        case ENHANCED_EW_TERMS:
            return BonusIndicatorUtils.getEnhancedEwTermsTitle(bonusInfo, stringResolver);
        default:
            return stringResolver.resolveString(`$BONUS_INDICATOR.${bonusType}.TITLE`);
    }
};

const getBonusText = (bonusType, currencyCode, stringResolver, bonusInfo) => {
    const messageResolver = {
        [ACCA_BOOST]: BonusIndicatorUtils.getAccaBoostMessage,
        [EVENT_TRIGGERED]: BonusIndicatorUtils.getEtbMessage,
        [EARLY_PAYOUT]: BonusIndicatorUtils.getEarlyPayoutMessage,
    };
    const getMessage = messageResolver[bonusType];
    const message = getMessage
        ? getMessage(bonusInfo || {}, stringResolver, currencyCode)
        : undefined;

    return message ? message : stringResolver.resolveString(`$BONUS_INDICATOR.${bonusType}.TEXT`);
};

BonusIndicator.getStyle = (config, mode, merge) => {
    return merge(config, {
        // Create an object with labeled tooltip configurations for each bonus type
        // whereby we only override the icon source for each bonus type
        labeledTooltips: mapValues(config.bonusTypes, bonusTypeConfig => {
            return merge(config.labeledTooltip, { icon: { src: bonusTypeConfig?.icon } });
        }),
    });
};

export default BonusIndicator;
